import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Grid } from '@material-ui/core';
import { GENDER_OPTIONS } from '../../../constants';
import SelectWithValidation from '../../ValidationFormElements/SelectWithValidation';
import DateInputFieldWithValidation from '../../ValidationFormElements/DateInputFieldWithValidation';
import TextInputFieldWithValidation from '../../ValidationFormElements/TextInputFieldWithValidation';
import NonArmenianCountryForm from '../../../containers/CompensationContainer/components/NonArmenianCountryForm';
import useRegisterValidationFields from '../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../hooks/useUnRegisterValidationFields';
import { getMenuLabelWithTranslation } from '../../../helpers/selectHelpers';
import { isArmenianCountry } from '../../../helpers/countryHelper';

const ClaimApplicantDetails = ({
  formProps,
  countries,
  valueNames,
  initialData,
}) => {
  const { t, i18n } = useTranslation();

  const {
    errors,
    clearError,
    getValues,
    setValue,
    register,
    unregister,
    triggerValidation,
  } = formProps;

  const {
    citizenshipKey,
    birthdayKey,
    firstNameKey,
    lastNameKey,
    passportKey,
    passportGivenDateKey,
    passportValidUntilKey,
    passportFromKey,
    genderKey,
    socCardKey,
  } = valueNames;

  useRegisterValidationFields(
    [
      citizenshipKey,
      birthdayKey,
      firstNameKey,
      lastNameKey,
      genderKey,
      passportKey,
      socCardKey,
    ],
    register,
  );

  useUnRegisterValidationFields(
    [
      citizenshipKey,
      birthdayKey,
      firstNameKey,
      lastNameKey,
      passportFromKey,
      passportGivenDateKey,
      genderKey,
      passportKey,
      socCardKey,
    ],
    unregister,
  );

  const [citizenship, setCitizenship] = useState(initialData[citizenshipKey] || null);
  const [passportFrom, setPassportFrom] = useState(initialData[passportFromKey]);
  const [birthday, setBirthday] = useState(initialData[birthdayKey] || null);
  const [passportGivenDate, setPassportGivenDate] = useState(
    initialData[passportGivenDateKey] || null,
  );
  const [gender, setGender] = useState(initialData[genderKey] || null);
  const [passportValidUntil, setPassportValidUntil] = useState(
    initialData[passportValidUntilKey] || null,
  );

  useEffect(() => {
    setValue(citizenshipKey, citizenship, !!citizenship);
  }, [setValue, citizenship, citizenshipKey]);

  useEffect(() => {
    setValue(passportKey, initialData[passportKey], !!initialData[passportKey]);
  }, [initialData, passportKey, setValue]);

  useEffect(() => {
    setValue(lastNameKey, initialData[lastNameKey], !!initialData[lastNameKey]);
  }, [initialData, lastNameKey, setValue]);

  useEffect(() => {
    setValue(firstNameKey, initialData[firstNameKey], !!initialData[firstNameKey]);
  }, [initialData, firstNameKey, setValue]);

  useEffect(() => {
    const newDate = birthday ? format(birthday, 'yyyy-MM-dd') : null;
    setValue(birthdayKey, newDate, !!newDate);
  }, [setValue, birthday, birthdayKey]);

  useEffect(() => {
    setValue(genderKey, gender, !!gender);
  }, [setValue, gender, genderKey]);

  const onChange = (key, value) => {
    setValue(key, value, !!value);
  };

  const getLabelWithTranslation = useCallback(
    (option) => getMenuLabelWithTranslation(option, i18n.language),
    [i18n],
  );

  return (
    <>
      <Grid container spacing={4}>
        <Grid item md={6} lg={4} xs={12}>
          <SelectWithValidation
            formProps={{
              errors,
              clearError,
            }}
            value={citizenship || ''}
            options={countries}
            getMenuLabel={getLabelWithTranslation}
            onChange={(country) => {
              setCitizenship(country);

              if (getValues()[passportKey]) {
                triggerValidation({ name: passportKey });
              }

              if (!isArmenianCountry(country)) {
                register({ name: passportGivenDateKey });
                register({ name: passportValidUntilKey });
                register({ name: passportFromKey });
              } else {
                unregister([passportGivenDateKey, passportValidUntilKey, passportFromKey]);
              }
            }}
            label="insurance.citizen"
            required
            name={citizenshipKey}
          />
        </Grid>
        <Grid item md={6} lg={4} xs={12}>
          <DateInputFieldWithValidation
            formProps={{ errors, clearError }}
            labelKey="insurance.dateOfBirth"
            name={birthdayKey}
            value={birthday ? new Date(birthday) : null}
            handleChange={(date) => {
              // eslint-disable-next-line no-restricted-globals
              if (date instanceof Date && !isNaN(date)) {
                setBirthday(date);
              }
            }}
            maxDate={new Date()}
            required
          />
        </Grid>
        <Grid item md={6} lg={4} xs={12}>
          <SelectWithValidation
            required
            name={genderKey}
            label="personalClaim.personalData.gender"
            value={GENDER_OPTIONS.find((option) => option.key === gender) || ''}
            formProps={{
              errors,
              clearError,
            }}
            getMenuLabel={(option) => t(option.label)}
            options={GENDER_OPTIONS}
            onChange={(data) => {
              setGender(data.value);
            }}
          />
        </Grid>
        <Grid item md={6} lg={4} xs={12}>
          <TextInputFieldWithValidation
            required
            formProps={{
              errors,
              clearError,
            }}
            labelKey="claim.personalData.affectedFName"
            name={firstNameKey}
            handleChange={({ target }) => onChange(target.name, target.value)}
            register={register}
          />
        </Grid>
        <Grid item md={6} lg={4} xs={12}>
          <TextInputFieldWithValidation
            required
            formProps={{
              errors,
              clearError,
            }}
            labelKey="claim.personalData.affectedLName"
            name={lastNameKey}
            handleChange={({ target }) => onChange(target.name, target.value)}
            register={register}
          />
        </Grid>
        <Grid item md={6} lg={4} xs={12}>
          <TextInputFieldWithValidation
            required
            formProps={{
              errors,
              clearError,
            }}
            labelKey="claim.personalData.passNum"
            name={passportKey}
            handleChange={({ target }) => onChange(target.name, target.value)}
            register={register}
          />
        </Grid>
        <Grid item md={6} lg={4} xs={12}>
          <TextInputFieldWithValidation
            required={!!isArmenianCountry(citizenship)}
            formProps={{ errors, clearError }}
            labelKey="claim.ssn"
            name={socCardKey}
            register={register}
            handleChange={({ target }) => onChange(target.name, target.value)}
          />
        </Grid>
        {citizenship && !isArmenianCountry(citizenship) && (
          <Grid item md={12} xs={12}>
            <NonArmenianCountryForm
              clearError={clearError}
              errors={errors}
              register={register}
              setValue={setValue}
              unregister={unregister}
              passportFromKey={passportFromKey}
              passportGivenDate={passportGivenDate}
              passportGivenDateKey={passportGivenDateKey}
              passportValidUntil={passportValidUntil}
              passportValidUntilKey={passportValidUntilKey}
              setPassportValidUntil={setPassportValidUntil}
              setPassportGivenDate={(newDate) => {
                setPassportGivenDate(newDate);
              }}
              passportFrom={passportFrom}
              setPassportFrom={(value) => setPassportFrom(value)}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

ClaimApplicantDetails.propTypes = {
  valueNames: PropTypes.shape({
    citizenshipKey: PropTypes.string,
    birthdayKey: PropTypes.string,
    firstNameKey: PropTypes.string,
    lastNameKey: PropTypes.string,
    passportKey: PropTypes.string,
    passportGivenDateKey: PropTypes.string,
    passportValidUntilKey: PropTypes.string,
    passportFromKey: PropTypes.string,
    genderKey: PropTypes.string,
    socCardKey: PropTypes.string,
  }),
  initialData: PropTypes.shape({}).isRequired,
  formProps: PropTypes.shape({
    register: PropTypes.func.isRequired,
    unregister: PropTypes.func.isRequired,
    getValues: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    errors: PropTypes.shape({}).isRequired,
    clearError: PropTypes.func.isRequired,
    triggerValidation: PropTypes.func.isRequired,
  }).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

ClaimApplicantDetails.defaultProps = {
  valueNames: {
    citizenshipKey: 'applicantCitizenship',
    birthdayKey: 'applicantBDay',
    firstNameKey: 'applicantFName',
    lastNameKey: 'applicantLName',
    passportKey: 'applicantPassNum',
    passportGivenDateKey: 'applicantPassGivenDate',
    passportValidUntilKey: 'applicantPassValidUntilDate',
    passportFromKey: 'applicantPassFrom',
    genderKey: 'applicantGender',
    socCardKey: 'applicantSocCard',
  },
};

export default ClaimApplicantDetails;
